/**
 * The material list on a project SKU — Max, 2026-09-02.
 *
 * "trong phần SKU sẽ có 1 phần là Material list… user sẽ điền material như vải, buckle, slipper hay
 * frame… từ đó có thể dùng cho Ticket code và sample tracker."
 *
 * TYPED, NOT PICKED, and that is a finding rather than a shortcut: the portal's material master
 * holds 4,723 raw items, but they are finished and semi-finished goods, retail items and FRAMES —
 * fabric, buckle and slider are not in there (a search for BUCKLE returns zero rows). A picker off
 * SAP would offer the frame and nothing else a development SKU needs. `SAP code` is the optional
 * column for the ones that do exist.
 *
 * It has its own table and its own endpoint, but it is saved by the form's Save button and by
 * nothing else (Max, 2026-09-03) — the typing is staged in `skuMaterialDrafts`, which the form owns,
 * so a collapsed SKU card keeps it and Cancel throws it away with the rest of the form.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { MATERIAL_API } from './skuMaterialDrafts';

/* One place for the column widths: the frozen offsets in the stylesheet are these numbers added up,
   so a change here without one there puts two columns on top of each other. */
const SKM_W = {
  kind: 120, name: 220, colour: 130, rmReady: 150, supplier: 150, qty: 80,
  uom: 80, sapCode: 110, spec: 220, note: 200, drop: 40,
};

const BLANK = {
  kind: '', name: '', spec: '', colour: '', supplier: '', qty: '', uom: '', sapCode: '', note: '',
};

/* HIDDEN, not removed — Max, 2026-09-05: "tôi nghĩ ko cần mấy trường này… nên hide đi nhé".
   The four keys stay on every row object, so they load, ride through an edit and save again
   untouched; nothing already typed is erased and putting one back on screen is deleting its name
   from this set. The database columns are untouched for the same reason. */
const HIDDEN = new Set(['supplier', 'qty', 'uom', 'sapCode']);
const show = (key) => !HIDDEN.has(key);
/* `readyDates` turns on one extra column that does NOT belong to the SKU: the RM ready date the
   round holds for each material (Max, 2026-09-05 — "cần có cột RM ready date nhé"). It is passed in
   rather than fetched because it is round data and this component knows only the SKU; the round form
   owns it and saves it with its own button. Absent on the SKU form, where there is no round to hold
   a date. Sits immediately after the frozen block so it is the first thing in view. */
export default function SkuMaterialList({
  skuId, canEdit = true, drafts = null, readyDates = null, onReadyDate = null,
}) {
  const { t } = useTranslation();
  const withReady = !!readyDates;
  /* The drop button's column only exists to hold the button. Read-only — the round showing the SKU's
     list — has no button, so it was an empty 40px strip on the right of every row (Max, 2026-09-05:
     "ẩn luôn khúc này nhé"). Kind, Material, Colour, Supplier, Qty, UoM, SAP code, Spec, Note is
     nine; RM ready and the drop column come and go, and HIDDEN takes the rest away. */
  const visibleCols = 9 + (withReady ? 1 : 0) + (canEdit ? 1 : 0) - HIDDEN.size;
  /* The table's minimum is the visible columns added up, and it is set here rather than in the
     stylesheet because THIS file decides which columns exist. It was a hardcoded 1350px — the sum of
     all ten — so hiding four left the table 310px wider than its own content and scrolling a list
     that now fits. Under table-layout: fixed a minimum smaller than the sum squeezes the columns
     instead of scrolling them, so it has to be the sum exactly. */
  const minWidth = ['kind', 'name', 'colour', ...(withReady ? ['rmReady'] : []),
    'supplier', 'qty', 'uom', 'sapCode', 'spec', 'note', ...(canEdit ? ['drop'] : [])]
    .filter((k) => !HIDDEN.has(k))
    .reduce((sum, k) => sum + SKM_W[k], 0);
  const [rows, setRows] = useState([]);
  const [kinds, setKinds] = useState([]);
  const [error, setError] = useState('');

  /* The kinds always come from the server. The rows come from the form's staging store if this card
     has been opened and typed in before — reloading them would throw that typing away the moment a
     SKU card is collapsed and opened again. */
  const load = useCallback(async () => {
    if (!skuId) return;
    setError('');
    const staged = drafts && drafts.get(skuId);
    try {
      const response = await fetch(`${MATERIAL_API}/${encodeURIComponent(skuId)}`, { credentials: 'include' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('xMat.loadFailed'));
      setKinds(data.kinds || []);
      setRows(staged || data.materials || []);
    } catch (err) { setError(err.message); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skuId]);
  useEffect(() => { load(); }, [load]);

  /* Every edit goes to the store as well as to the screen, because the store is what Save reads. */
  const edit = (next) => {
    setRows(next);
    if (drafts && canEdit) drafts.put(skuId, next);
  };
  const set = (i, key) => (e) =>
    edit(rows.map((r, x) => (x === i ? { ...r, [key]: e.target.value } : r)));
  const addRow = () => edit([...rows, { ...BLANK }]);
  const dropRow = (i) => edit(rows.filter((_, x) => x !== i));

  /* A row with no name is not a material — the server drops those, so say it here rather than
     letting somebody wonder where the row went. Only once something is typed: an empty row that was
     just added is not a mistake yet. */
  const unnamed = rows.filter((r) => !String(r.name || '').trim()
    && Object.values(r).some((v) => String(v || '').trim())).length;

  return (
    <div className="skm">
      <div className="skm-head">
        <strong>{t('xMat.title')}</strong>
        <small>{rows.length ? t('xMat.itemsCount', { n: rows.length }) : t('xMat.nothingYet')}</small>
        {/* No Save button and no unsaved marker (Max, 2026-09-03) — the form's Save writes this list
            too, so the only button here is the one that adds a row, at the end of the bar. */}
        {canEdit && (
          <button type="button" className="sp-mini-btn" onClick={addRow}>
            <Plus size={12} /> {t('xMat.addMaterial')}
          </button>
        )}
      </div>
      {!!error && <div className="sp-error">{error}</div>}
      {!!unnamed && (
        <div className="skm-warn">
          {t('xMat.unnamedWarn', { n: unnamed })}
        </div>
      )}

      <div className="skm-scroll">
        <table className="sp-table skm-table" style={{ minWidth }}>
          {/* Declared widths, because the first three columns are frozen and a sticky cell needs a
              real offset — measuring live widths would have the header and the body disagree
              mid-scroll. Spec sits by Note at the end (Max, 2026-09-03): it is a sentence, and it was
              pushing Colour and Supplier out of view on a list read for what and how many. */}
          <colgroup>
            <col style={{ width: SKM_W.kind }} />
            <col style={{ width: SKM_W.name }} />
            <col style={{ width: SKM_W.colour }} />
            {withReady && <col style={{ width: SKM_W.rmReady }} />}
            {show('supplier') && <col style={{ width: SKM_W.supplier }} />}
            {show('qty') && <col style={{ width: SKM_W.qty }} />}
            {show('uom') && <col style={{ width: SKM_W.uom }} />}
            {show('sapCode') && <col style={{ width: SKM_W.sapCode }} />}
            <col style={{ width: SKM_W.spec }} />
            <col style={{ width: SKM_W.note }} />
            {canEdit && <col style={{ width: SKM_W.drop }} />}
          </colgroup>
          <thead>
            <tr>
              <th className="skm-fz skm-fz1">{t('xMat.colKind')}</th>
              <th className="skm-fz skm-fz2">{t('xMat.colMaterial')}</th>
              <th className="skm-fz skm-fz3 skm-fz-last">{t('xMat.colColour')}</th>
              {withReady && <th>{t('xMat.colRmReady')}</th>}
              {show('supplier') && <th>{t('xMat.colSupplier')}</th>}
              {show('qty') && <th className="num">{t('xMat.colQty')}</th>}
              {show('uom') && <th>{t('xMat.colUom')}</th>}
              {show('sapCode') && <th>{t('xMat.colSapCode')}</th>}
              <th>{t('xMat.colSpec')}</th><th>{t('xMat.colNote')}</th>{canEdit && <th />}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={i}>
                <td className="skm-fz skm-fz1">
                  <input list="skm-kinds" value={r.kind} disabled={!canEdit} onChange={set(i, 'kind')} />
                </td>
                <td className="skm-fz skm-fz2"><input value={r.name} disabled={!canEdit} onChange={set(i, 'name')}
                  placeholder="600D polyester, YKK #5 slider…" /></td>
                <td className="skm-fz skm-fz3 skm-fz-last"><input value={r.colour} disabled={!canEdit} onChange={set(i, 'colour')} /></td>
                {withReady && (
                  <td className="skm-ready">
                    {/* Keyed by the row's uid, not by its position: `seq` is renumbered on every save
                        of the list, so a date pinned to a position would move to another material the
                        first time a row was inserted or removed. A row typed but never saved has no
                        uid yet and cannot carry a date — it gets one when the list is saved. */}
                    <input
                      type="date"
                      value={(readyDates || {})[r.id] || ''}
                      disabled={!onReadyDate || !r.id}
                      title={r.id ? '' : t('xMat.rmNeedsSave')}
                      onChange={(e) => onReadyDate && onReadyDate(r.id, e.target.value)}
                    />
                  </td>
                )}
                {show('supplier') && <td><input value={r.supplier} disabled={!canEdit} onChange={set(i, 'supplier')} /></td>}
                {show('qty') && <td><input type="number" step="any" value={r.qty} disabled={!canEdit} onChange={set(i, 'qty')} /></td>}
                {show('uom') && <td><input value={r.uom} disabled={!canEdit} onChange={set(i, 'uom')} placeholder="m, pcs" /></td>}
                {show('sapCode') && (
                  <td><input value={r.sapCode} disabled={!canEdit} onChange={set(i, 'sapCode')}
                    placeholder={t('xMat.sapPh')} /></td>
                )}
                <td><input value={r.spec} disabled={!canEdit} onChange={set(i, 'spec')} /></td>
                <td><input value={r.note} disabled={!canEdit} onChange={set(i, 'note')} /></td>
                {canEdit && (
                  <td>
                    <button type="button" className="sp-mini-btn sp-mini-danger" onClick={() => dropRow(i)}>
                      <Trash2 size={12} />
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {!rows.length && (
              <tr><td className="sp-empty" colSpan={visibleCols}>
                {t('xMat.empty')}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      {/* The kinds come from a dim table, so the list is editable on the Master data screen rather
          than in this file — and the input stays free text for anything not on it yet. */}
      <datalist id="skm-kinds">
        {kinds.map((k) => <option key={k} value={k} />)}
      </datalist>
    </div>
  );
}
